'use client'

import { cn } from '@/lib/utils'
import { Bookmark, Check, ChevronDown, Filter, Heart } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

interface Tag {
  id: string
  name: string
}

type FilterVariant = 'bookmark' | 'favorite'

interface FilterDropdownProps {
  showBookmarkedOnly: boolean
  selectedTagIds: string[]
  availableTags: Tag[]
  onBookmarkToggle: () => void
  onTagToggle: (tagId: string) => void
  onClearFilters: () => void
  variant?: FilterVariant
}

const variantConfig = {
  bookmark: {
    label: 'Bookmarked',
    icon: Bookmark,
    activeColor: 'bg-blue-50 text-blue-700 hover:bg-blue-100',
    iconActiveColor: 'fill-blue-600 text-blue-600',
  },
  favorite: {
    label: 'Favorited',
    icon: Heart,
    activeColor: 'bg-red-50 text-red-700 hover:bg-red-100',
    iconActiveColor: 'fill-red-500 text-red-500',
  },
}

export function FilterDropdown({
  showBookmarkedOnly,
  selectedTagIds,
  availableTags,
  onBookmarkToggle,
  onTagToggle,
  onClearFilters,
  variant = 'bookmark',
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const config = variantConfig[variant]
  const Icon = config.icon

  const activeFilterCount = (showBookmarkedOnly ? 1 : 0) + selectedTagIds.length

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleClearAll = useCallback(() => {
    onClearFilters()
  }, [onClearFilters])

  return (
    <div ref={dropdownRef} className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'font-space-grotesk inline-flex h-[40px] items-center justify-center gap-x-2 truncate rounded-2xl border border-black/10 bg-white/80 px-4 text-sm font-medium shadow-sm backdrop-blur-xl transition hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300',
          activeFilterCount > 0 ? 'text-purple-700' : 'text-gray-900'
        )}
      >
        <Filter className="h-4 w-4" />
        <span>Filters</span>
        {activeFilterCount > 0 && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-600 text-xs text-white">
            {activeFilterCount}
          </span>
        )}
        <ChevronDown
          className={cn('h-4 w-4 text-gray-400 transition-transform', isOpen && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          className="absolute left-0 z-50 mt-2 w-56 origin-top-left rounded-2xl border border-black/10 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-xl focus:outline-none"
          role="menu"
          aria-orientation="vertical"
        >
          <div className="py-1">
            {/* Header with clear all button */}
            <div className="mb-3 flex items-center justify-between px-4">
              <span className="font-space-grotesk text-sm font-medium text-gray-500">
                Filter by
              </span>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="font-space-grotesk text-xs text-purple-600 hover:text-purple-700"
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Bookmark/Favorite Filter */}
            <div className="px-4">
              <button
                type="button"
                onClick={onBookmarkToggle}
                className={cn(
                  'font-space-grotesk flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                  showBookmarkedOnly ? config.activeColor : 'text-gray-700 hover:bg-gray-50'
                )}
              >
                <Icon className={cn('h-4 w-4', showBookmarkedOnly && config.iconActiveColor)} />
                <span className="flex-1 text-left">{config.label}</span>
                {showBookmarkedOnly && <Check className="h-4 w-4" />}
              </button>
            </div>

            {/* Separator */}
            {availableTags.length > 0 && (
              <hr className="my-2 border-gray-200 opacity-100 transition-all duration-300 ease-in-out" />
            )}

            {/* Tag Filters */}
            {availableTags.length > 0 && (
              <div className="px-4">
                <p className="font-space-grotesk mb-2 text-sm font-medium text-gray-500">Tags</p>
                <div className="space-y-1">
                  {availableTags.map((tag) => {
                    const isSelected = selectedTagIds.includes(tag.id)
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => onTagToggle(tag.id)}
                        className={cn(
                          'font-space-grotesk flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                          isSelected
                            ? 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                            : 'text-gray-700 hover:bg-gray-50'
                        )}
                      >
                        <div
                          className={cn(
                            'flex h-4 w-4 items-center justify-center rounded border',
                            isSelected
                              ? 'border-purple-600 bg-purple-600'
                              : 'border-gray-300 bg-white'
                          )}
                        >
                          {isSelected && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <span className="flex-1 text-left">{tag.name}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
